import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import Crown from "@/components/Crown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "na_admin_token";

const authHeader = (token) =>
  token ? { headers: { "X-Admin-Token": token } } : {};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [settings, setSettings] = useState({
    newsletter_webhook: "",
    freebie_webhook: "",
    contact_webhook: "",
  });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  // Auth
  const tryAuth = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`, authHeader(t));
      setSettings({
        newsletter_webhook: res.data.newsletter_webhook || "",
        freebie_webhook: res.data.freebie_webhook || "",
        contact_webhook: res.data.contact_webhook || "",
      });
      localStorage.setItem(TOKEN_KEY, t);
      setAuthed(true);
      // Load leads
      const lr = await axios.get(`${API}/admin/leads`, authHeader(t));
      setLeads(lr.data || []);
    } catch (e) {
      toast.error("Wrong admin token. Try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) tryAuth(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (token.trim()) await tryAuth(token.trim());
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    toast.success("Signed out.");
  };

  const saveSettings = async () => {
    setSavingKey("settings");
    try {
      await axios.post(`${API}/admin/settings`, settings, authHeader(token));
      toast.success("CRM webhook settings saved.");
    } catch (e) {
      toast.error("Could not save settings.");
    } finally {
      setSavingKey("");
    }
  };

  const testWebhook = async (url) => {
    if (!url.trim()) {
      toast.error("Paste a URL first.");
      return;
    }
    setSavingKey(url);
    try {
      const r = await axios.post(`${API}/admin/test-webhook`, { url }, authHeader(token));
      if (r.data.ok) {
        toast.success(`✓ Sent. Status ${r.data.status_code}`);
      } else {
        toast.error(`Failed: ${r.data.error}`);
      }
    } catch (e) {
      toast.error("Test request failed.");
    } finally {
      setSavingKey("");
    }
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const headers = ["created_at", "type", "first_name", "last_name", "email", "interest", "message"];
    const rows = leads.map((l) =>
      headers.map((h) => `"${(l[h] || "").toString().replace(/"/g, '""')}"`).join(","),
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `nijah-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ---------- Auth gate ----------
  if (!authed) {
    return (
      <div data-testid="page-admin-login" style={{ background: "var(--teal)", color: "#fff", minHeight: "calc(100vh - 67px)" }}>
        <div className="container-x" style={{
          maxWidth: 460, padding: "120px 20px", margin: "0 auto",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 18,
        }}>
          <Crown size={42} opacity={0.95} />
          <SectionLabel gold>ADMIN</SectionLabel>
          <h1 className="h-display" style={{ fontSize: "clamp(34px, 4vw, 52px)", color: "#fff", textAlign: "center" }}>
            Sign in to <span className="italic-c text-gold">manage leads.</span>
          </h1>
          <form onSubmit={onLogin} className="form-row" style={{ width: "100%", maxWidth: 380 }}>
            <input
              type="password"
              className="input-base"
              placeholder="Admin token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              data-testid="admin-token-input"
            />
            <button className="btn btn-primary" type="submit" disabled={loading} data-testid="admin-signin">
              {loading ? "Verifying…" : "Sign In →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Authed view ----------
  return (
    <div data-testid="page-admin" style={{ minHeight: "calc(100vh - 67px)", background: "var(--cream)" }}>
      <section style={{ background: "var(--teal)", color: "#fff", padding: "60px 0 40px" }}>
        <div className="container-x">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
            <div>
              <SectionLabel gold>ADMIN</SectionLabel>
              <h1 className="h-display" style={{ fontSize: "clamp(30px, 3.4vw, 44px)", color: "#fff", marginTop: 8 }}>
                CRM &amp; Leads <span className="italic-c text-gold">Dashboard.</span>
              </h1>
            </div>
            <button className="btn btn-ghost" onClick={onLogout} data-testid="admin-signout">Sign out</button>
          </div>
        </div>
      </section>

      {/* CRM webhook settings */}
      <section className="section-pad bg-cream">
        <div className="container-x" style={{ maxWidth: 880 }}>
          <SectionLabel>CRM WEBHOOKS</SectionLabel>
          <h2 className="section-title text-teal">
            Connect your <span className="italic-c text-coral">CRM.</span>
          </h2>
          <p className="body-base" style={{ marginBottom: 28 }}>
            Paste a webhook URL for each form. Every new submission will be POSTed
            there as JSON ({"{ source, type, first_name, email, ... }"}). Works
            with Zapier, Make, ConvertKit, Mailchimp, GoHighLevel, HubSpot, n8n,
            or any custom endpoint.
          </p>

          {[
            { key: "freebie_webhook",    label: "Lead Magnet (Mirror Method) Webhook", note: "Fires when someone submits the free-guide form." },
            { key: "newsletter_webhook", label: "Newsletter Webhook",                  note: "Fires when someone subscribes to the newsletter." },
            { key: "contact_webhook",    label: "Contact / Application Webhook",        note: "Fires when someone submits the contact form." },
          ].map((row) => (
            <div key={row.key} className="webhook-row" data-testid={`webhook-${row.key}`}>
              <label className="form-label">{row.label}</label>
              <p style={{ fontSize: 12, color: "rgba(13,31,36,.6)", marginBottom: 8 }}>{row.note}</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="url"
                  className="input-base"
                  placeholder="https://hooks.zapier.com/hooks/catch/…"
                  value={settings[row.key]}
                  onChange={(e) => setSettings({ ...settings, [row.key]: e.target.value })}
                  style={{ flex: "1 1 320px" }}
                  data-testid={`webhook-input-${row.key}`}
                />
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => testWebhook(settings[row.key])}
                  disabled={savingKey === settings[row.key]}
                  data-testid={`webhook-test-${row.key}`}
                >
                  Send Test Ping
                </button>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 22 }}>
            <button
              className="btn btn-teal"
              onClick={saveSettings}
              disabled={savingKey === "settings"}
              data-testid="webhook-save"
            >
              {savingKey === "settings" ? "Saving…" : "Save Webhooks"}
            </button>
          </div>
        </div>
      </section>

      {/* Leads list */}
      <section className="section-pad bg-blush">
        <div className="container-x" style={{ maxWidth: 1100 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
            <div>
              <SectionLabel>CAPTURED LEADS</SectionLabel>
              <h2 className="section-title text-teal">
                {leads.length} <span className="italic-c text-coral">total submissions.</span>
              </h2>
            </div>
            <button className="btn btn-outline-dark" onClick={exportCsv} disabled={!leads.length} data-testid="admin-export-csv">
              Export CSV
            </button>
          </div>

          <div className="leads-table" data-testid="leads-table">
            <div className="leads-row leads-head">
              <div>Date</div>
              <div>Type</div>
              <div>Name</div>
              <div>Email</div>
              <div>Details</div>
            </div>
            {leads.map((l, i) => (
              <div className="leads-row" key={l.id || i}>
                <div>{(l.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div><span className={`type-pill type-${l.type}`}>{l.type}</span></div>
                <div>{[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}</div>
                <div><a href={`mailto:${l.email}`} style={{ color: "var(--teal)" }}>{l.email}</a></div>
                <div style={{ color: "rgba(13,31,36,.7)", fontSize: 13 }}>
                  {l.interest && <div><strong>Interest:</strong> {l.interest}</div>}
                  {l.message && <div style={{ marginTop: 4 }}>{l.message}</div>}
                </div>
              </div>
            ))}
            {!leads.length && (
              <div style={{ padding: 30, textAlign: "center", color: "rgba(13,31,36,.55)" }}>
                No submissions yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admin;
